import React, { useEffect, useState, Fragment } from "react";
import { path, pipe, prop, sortBy } from "ramda";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { isBlank, notBlank } from "common/general";
import {
  CONTRIBUTOR_FIELD_BLURRED,
  CONTRIBUTOR_FIELD_CHANGED,
  CONTRIBUTOR_ROLE_CHANGED,
} from "basic-details/basicDetailsActions";
import {
  getContributorFields,
  getActiveStep,
  getStepErrors,
  getProjectContributorRoles,
} from "common/selectors";
import { firstError, hasError } from "common/validation";
import { useTranslation } from "react-i18next";

import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";

import CustomizableTextField from "../../general-components/CustomizableTextField";
import IntlAutocomplete from "../../general-components/IntlAutocomplete";
import IntlInputLabel from "../../general-components/IntlInputLabel";
import IntlSelect from "../../general-components/IntlSelect";

const useStyles = makeStyles(theme => ({
  heading: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.dark,
    marginTop: theme.spacing(3),
  },

  subheading: {
    fontSize: ".85rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
}));

const CreatorDetailsForm = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const roles = useSelector(getProjectContributorRoles);
  const [roleOptions, setRoleOptions] = useState([]);
  useEffect(() => {
    console.log(roles);

    const options = pipe(sortBy(prop("name")))(roles);
    setRoleOptions(options);
  }, [roles]);

  const activeStep = useSelector(getActiveStep);
  const errors = useSelector(getStepErrors(activeStep));
  const fields = useSelector(getContributorFields);

  const dispatch = useDispatch();

  const onBlur = (field, value) => () =>
    dispatch({ type: CONTRIBUTOR_FIELD_BLURRED, field, value });
  const onRoleChange = (event, role) =>
    dispatch({ type: CONTRIBUTOR_ROLE_CHANGED, role });
  const onFieldChange = path => event => {
    const value = event.target.value;
    dispatch({ type: CONTRIBUTOR_FIELD_CHANGED, path, value });
  };

  return (
    <form autoComplete="none">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <IntlInputLabel htmlFor="contributor-type">
                  {t("creator_details_contributor_type")}
                </IntlInputLabel>
                <IntlSelect
                  value={path(["type"], fields) || "PERSON"}
                  onChange={onFieldChange(["type"])}
                  inputProps={{
                    name: "contributor-type",
                    id: "contributor-type",
                  }}
                >
                  <MenuItem value="PERSON" key="PERSON">
                    {t("creator_details_contributor_type_person")}
                  </MenuItem>
                  <MenuItem value="CORPORATION" key="CORPORATION">
                    {t("creator_details_contributor_type_corporation")}
                  </MenuItem>
                </IntlSelect>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {fields.type !== "CORPORATION" ? (
          <Fragment>
            <CustomizableTextField
              id="contributor-prefix"
              fieldOption="prefix"
              xs={6}
              sm={4}
              defaultLabel="Prefix"
              value={fields.prefix || ""}
              onChange={onFieldChange(["prefix"])}
              error={hasError(errors, "prefix")}
              helperText={t(firstError(errors, "prefix"))}
              onBlur={onBlur("prefix", fields.prefix)}
              inputProps={{ maxLength: 50, id: "contributor-prefix" }}
            />

            <Grid item xs={12} sm={12}>
              <Grid container spacing={2}>
                <CustomizableTextField
                  id="contributor-first-name"
                  fieldOption="first_name"
                  xs={12}
                  sm={4}
                  defaultLabel={t("personal_details.first_name", "First name")}
                  value={fields.firstName || ""}
                  onChange={onFieldChange(["firstName"])}
                  error={hasError(errors, "firstName")}
                  helperText={t(firstError(errors, "firstName"))}
                  onBlur={onBlur("firstName", fields.firstName)}
                  inputProps={{ maxLength: 100, id: "contributor-first-name" }}
                />

                <CustomizableTextField
                  id="contributor-middle-name"
                  fieldOption="middle_name"
                  xs={12}
                  sm={4}
                  defaultLabel={t(
                    "personal_details.middle_name",
                    "Middle name"
                  )}
                  value={fields.middleName || ""}
                  onChange={onFieldChange(["middleName"])}
                  error={hasError(errors, "middleName")}
                  helperText={t(firstError(errors, "middleName"))}
                  onBlur={onBlur("middleName", fields.middleName)}
                  inputProps={{ maxLength: 100, id: "contributor-middle-name" }}
                />

                <CustomizableTextField
                  id="contributor-last-name"
                  fieldOption="last_name"
                  xs={12}
                  sm={4}
                  defaultLabel={t("personal_details.last_name", "Last name")}
                  value={fields.lastName || ""}
                  onChange={onFieldChange(["lastName"])}
                  error={hasError(errors, "lastName")}
                  helperText={t(firstError(errors, "lastName"))}
                  onBlur={onBlur("lastName", fields.lastName)}
                  inputProps={{ maxLength: 100, id: "contributor-last-name" }}
                />
              </Grid>
            </Grid>

            <CustomizableTextField
              id="contributor-suffix"
              fieldOption="suffix"
              xs={6}
              sm={4}
              defaultLabel={t("personal_details.suffix", "Suffix")}
              value={fields.suffix || ""}
              onChange={onFieldChange(["suffix"])}
              error={hasError(errors, "suffix")}
              helperText={t(firstError(errors, "suffix"))}
              onBlur={onBlur("suffix", fields.suffix)}
              inputProps={{ maxLength: 100, id: "contributor-suffix" }}
            />
          </Fragment>
        ) : (
          <Fragment>
            <CustomizableTextField
              id="contributor-corporation-name"
              fieldOption="corporation_name"
              xs={12}
              sm={12}
              defaultLabel={t(
                "personal_details.corporation_name",
                "Corporation Name"
              )}
              value={fields.corporationName || ""}
              onChange={onFieldChange(["corporationName"])}
              error={hasError(errors, "corporationName")}
              helperText={t(firstError(errors, "corporationName"))}
              onBlur={onBlur("corporationName", fields.corporationName)}
              inputProps={{
                maxLength: 400,
                id: "contributor-corporation-name",
              }}
            />
          </Fragment>
        )}

        <Grid item xs={12} sm={12}>
          <Grid container spacing={2}>
            <CustomizableTextField
              id="contributor-x.com-handle"
              fieldOption="twitter_handle"
              xs={6}
              sm={6}
              defaultLabel={t(
                "personal_details.twitter_handle",
                "X handle (optional)"
              )}
              value={fields.twitterHandle || ""}
              onChange={onFieldChange(["twitterHandle"])}
              error={hasError(errors, "twitterHandle")}
              helperText={t(firstError(errors, "twitterHandle"))}
              onBlur={onBlur("twitterHandle", fields.twitterHandle)}
              inputProps={{ maxLength: 30, id: "contributor-x.com-handle" }}
            />
            <CustomizableTextField
              id="contributor-website"
              fieldOption="website"
              xs={6}
              sm={6}
              defaultLabel={t(
                "personal_details.website_url",
                "Website URL (optional)"
              )}
              value={fields.website || ""}
              onChange={onFieldChange(["website"])}
              error={hasError(errors, "website")}
              helperText={t(firstError(errors, "website"))}
              onBlur={onBlur("website", fields.website)}
              inputProps={{ maxLength: 2048, id: "contributor-website" }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Grid container>
            <Grid item xs={12} sm={12}>
              <div className={classes.heading}>
                {t("creator_details_select_role")}
              </div>
              <div className={classes.subheading}>
                {t("creator_details_select_role_sub")}
              </div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <IntlAutocomplete
                id="contributor-role"
                disableClearable
                autoSelect
                options={roleOptions}
                value={path(["role"], fields) || ""}
                getOptionLabel={role => (notBlank(role) ? role.display : "")}
                getOptionSelected={(opt, val) =>
                  isBlank(val) || opt.name === val.name
                }
                onChange={onRoleChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = "contributor-role";
                  return (
                    <CustomizableTextField
                      params={params}
                      fieldOption="role"
                      onBlur={onBlur("role", path(["role"], fields))}
                      error={hasError(errors, "role")}
                      helperText={t(firstError(errors, "role"))}
                      name={"role-" + Math.random()}
                      defaultLabel={t("personal_details.role", "Role")}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default CreatorDetailsForm;
